import { Input } from "@/frontend/shared/ui/Input";
import { Modal } from "@/frontend/shared/ui/Modal";
import { Typography } from "@/frontend/shared/ui/Typography";
import { useState } from "react";
import styles from "./AddWebsiteModal.module.scss";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { TCompanyData } from "../../model/types/types";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { validateWebsite } from "@/frontend/shared/lib/validations/validateWebsite";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";
import { Logo } from "@/frontend/shared/ui/Logo";
import { WebIcon } from "@/frontend/shared/assets/icons/WebIcon";
import { getCheckDomain } from "../../model/getCheckDomain";
import { patchUpdateDetails } from "../../model/patchUpdateDetails";

export enum EAddWebsiteModalSection {
  ADD_WEBSITE = "add website",
  EDIT_DOMAIN = "edit domain"
}

export function AddWebsiteModal({
  isOpen,
  onClose,
  id,
  triggerRefetch,
  existedWebsite
}: {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  triggerRefetch: () => void;
  existedWebsite: string;
}) {
  const [shownSection, setShownSection] = useState<{ section: EAddWebsiteModalSection; data: TCompanyData | null }>({
    section: EAddWebsiteModalSection.ADD_WEBSITE,
    data: null
  });
  const [domain, setDomain] = useState(existedWebsite);
  const [isCheckingDomainLoading, setIsCheckingDomainLoading] = useState(false);
  const [isSyncPorfileLoading, setIsSyncPorfileLoading] = useState(false);
  const [isEnteringDifferentWebsite, setIsEnteringDifferentWebsite] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const { website, name, linkedInUrl, logoUrl, headline } = shownSection.data || {};

  function onCloseModal() {
    if (shownSection.section === EAddWebsiteModalSection.EDIT_DOMAIN) {
      setShownSection({ section: EAddWebsiteModalSection.ADD_WEBSITE, data: null });
      setDomain(existedWebsite);
    } else {
      if (domain.length > 0 && domain !== existedWebsite) {
        setIsOpenConfirmationModal(true);
      } else {
        onClose();
      }
    }
  }

  function onConfirmDiscardChanges() {
    setIsOpenConfirmationModal(false);
    setDomain(existedWebsite);
    onClose();
  }

  async function onAddWebsite() {
    const error = validateWebsite({ website: domain });

    if (error) {
      snackbarConstructor(error, "error");
      return;
    }

    if (domain === existedWebsite) {
      snackbarConstructor("Website URL is the same", "error");
      return;
    }

    setIsCheckingDomainLoading(true);

    if (isEnteringDifferentWebsite) {
      setShownSection({ section: EAddWebsiteModalSection.ADD_WEBSITE, data: null });
      try {
        await patchUpdateDetails({ id, domain });
      } catch (e) {
        reportErrorToSentry(e as AxiosError, "/portfolio-companies/${id}/company-details");
        snackbarConstructor("Failed to add website", "error");
      }
      triggerRefetch();
      onClose();
      setIsEnteringDifferentWebsite(false);
      setIsCheckingDomainLoading(false);
    } else {
      try {
        const res = await getCheckDomain({ domain: domain });
        if (res) {
          setShownSection({ section: EAddWebsiteModalSection.EDIT_DOMAIN, data: res });
        }
      } catch (e) {
        const axiosError = e as AxiosError<{ message: string }>;

        if (axiosError.response?.status === 404) {
          try {
            await patchUpdateDetails({ id, domain });
            triggerRefetch();
            snackbarConstructor("Website added successfully", "normal");
            onClose();
          } catch (e) {
            reportErrorToSentry(e as AxiosError, "/portfolio-companies/${id}/company-details");
            snackbarConstructor(axiosError?.response?.data.message || "Failed to add website", "error");
          }
        }
      } finally {
        setIsCheckingDomainLoading(false);
      }
    }
  }

  async function onSyncProfile() {
    setIsSyncPorfileLoading(true);
    try {
      await patchUpdateDetails({ id, domain: website || "" });
      triggerRefetch();
    } catch (e) {
      reportErrorToSentry(e as AxiosError, "/portfolio-companies/${id}/company-details");
      snackbarConstructor("Failed to sync profile", "error");
    } finally {
      setIsSyncPorfileLoading(false);
      setShownSection({ section: EAddWebsiteModalSection.ADD_WEBSITE, data: null });
      setDomain("");
      onClose();
    }
  }

  function onEnterDifferentWebsite() {
    setIsEnteringDifferentWebsite(true);
    setShownSection({ section: EAddWebsiteModalSection.ADD_WEBSITE, data: null });
  }

  const content =
    shownSection.section === EAddWebsiteModalSection.ADD_WEBSITE ? (
      <>
        <div>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            inputSize="small"
            placeholder="Website URL"
            className={styles.websiteInput}
          />
          <Typography variant="body-3" color="greyscale500" className={styles.description}>
            We use the website as a unique identifier and for founders’ invites. It will serve as the domain name needed
            to invite the founder.
          </Typography>
        </div>

        <div className={styles.bottomButtonContainer}>
          <CancelButton size="large" onClick={onCloseModal}>
            Cancel
          </CancelButton>
          <NormalButton size="large" onClick={onAddWebsite} isSubmitting={isCheckingDomainLoading}>
            Save
          </NormalButton>
        </div>
      </>
    ) : (
      <>
        <div className={styles.editDomainContainer}>
          <Typography variant="body-2" color="greyscale700">
            It looks like this website is already associated with a company in our public database.
          </Typography>
          <div className={styles.companyContainer}>
            <Logo logo={logoUrl} />
            <div className={styles.companyInfo}>
              <Typography variant="label-3" color="greyscale900">
                {name}
              </Typography>
              <Typography className={styles.companyDescription} variant="body-3" color="greyscale700">
                {headline}
              </Typography>
              {!!website && (
                <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
                  <WebIcon />
                  <Typography variant="label-5" color="greyscale700">
                    {website}
                  </Typography>
                </NormalButton>
              )}
            </div>
          </div>
        </div>
        <div className={styles.bottomButtonContainer}>
          <CancelButton size="large" onClick={onEnterDifferentWebsite}>
            Use Different Domain
          </CancelButton>
          <NormalButton
            size="large"
            onClick={onSyncProfile}
            isSubmitting={isSyncPorfileLoading}
            className={styles.syncButton}
          >
            Sync Profile
          </NormalButton>
        </div>
      </>
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={
        shownSection.section === EAddWebsiteModalSection.ADD_WEBSITE ? "Add Website" : "Domain name already exists"
      }
      width={shownSection.section === EAddWebsiteModalSection.EDIT_DOMAIN ? "516px" : "454px"}
      className={styles.modal}
    >
      {content}
      <ConfirmationModal
        isOpen={isOpenConfirmationModal}
        onClose={() => setIsOpenConfirmationModal(false)}
        onConfirm={onConfirmDiscardChanges}
        content={"Are you sure you want to discard the changes you made?"}
        title={"Discard Changes"}
        confirmButtonText={"Discard"}
        dangerConfirm
        cancelButtonText={"No Thanks"}
      />
    </Modal>
  );
}
